import React, { CSSProperties, useMemo } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import {
  FormControl,
  Select,
  SelectProps,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  SelectChangeEvent,
  Typography,
  Stack,
  Chip,
} from '@mui/material';

import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';
import CaretDownIcon from '../icons/CaretDownIcon';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

type SelectValue = (string | number)[];

interface MultiSelectProps<T extends FieldValues = FieldValues> extends Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> {
  name: string;
  control: Control<T>;
  options: SelectOption[];
  label: string;
  required?: boolean;
  defaultValue?: SelectValue;
  disabled?: boolean;
  loading?: boolean;
  style?: CSSProperties;
  menuHeight?: number;
  onChange?: (value: SelectValue) => void;
}

export const InputSelectFilterParamsField = <T extends FieldValues = FieldValues>({
  name,
  control,
  options,
  label,
  required = false,
  defaultValue = [],
  disabled = false,
  loading = false,
  onChange,
  style,
  menuHeight = 300,
  ...selectProps
}: MultiSelectProps<T>) => {
  const { t } = useLocalization();

  const allValues = useMemo(() => options.filter(opt => !opt.disabled).map(opt => opt.value), [options]);

  return (
    <Controller
      name={name as any}
      control={control}
      defaultValue={defaultValue as any}
      render={({ field: { value, onChange: fieldOnChange, onBlur: fieldOnBlur }, fieldState: { error } }) => {
        const selectedValues: SelectValue = Array.isArray(value) ? value : [];

        const isAllSelected =
          allValues.length > 0 &&
          allValues.length === selectedValues.length &&
          allValues.every(v => selectedValues.includes(v));

        const handleSelectAllToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
          const checked = event.target.checked;
          const newValues = checked ? allValues : [];
          fieldOnChange(newValues);
          onChange?.(newValues);
        };

        const handleChange = (event: SelectChangeEvent<unknown>) => {
          const vals = event.target.value as SelectValue;
          fieldOnChange(vals);
          onChange?.(vals);
        };

        const hasSelectedValues = selectedValues.length > 0;

        return (
          <FormControl
            variant="outlined"
            fullWidth
            error={Boolean(error)}
            disabled={disabled || loading}
            style={style}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasSelectedValues ? 'primary.main' : 'inherit',
                },
              },
            }}
          >
            <Select
              multiple
              value={selectedValues}
              onBlur={fieldOnBlur}
              onChange={handleChange}
              renderValue={selected => (
                <Stack direction="row" alignItems="center" gap={1}>
                  {label}
                  {hasSelectedValues && <Chip label={selectedValues.length} color="primary" size="small" />}
                </Stack>
              )}
              MenuProps={{
                PaperProps: { style: { maxHeight: menuHeight } },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              IconComponent={props => <CaretDownIcon {...props} />}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  py: '0.5rem',
                },
                '& .MuiSelect-icon': {
                  right: '0.75rem',
                },
              }}
              {...selectProps}
            >
              <MenuItem disableRipple>
                <FormControlLabel
                  control={<Checkbox checked={isAllSelected} onChange={handleSelectAllToggle} size="small" />}
                  label={
                    <Typography variant="h5" sx={{ fontWeight: 550 }}>
                      {t('common.terms.selectAll')}
                    </Typography>
                  }
                  sx={{ width: '100%', margin: 0, pl: 0 }}
                  onClick={event => event.stopPropagation()}
                />
              </MenuItem>
              {options.map(opt => {
                const isChecked = selectedValues.includes(opt.value);
                return (
                  <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                    <Checkbox checked={isChecked} disabled={opt.disabled} size="small" />
                    <ListItemText primary={t(opt.label)} />
                  </MenuItem>
                );
              })}
            </Select>
            <TextFormError error={error} />
          </FormControl>
        );
      }}
    />
  );
};
